import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiCalendar, FiCreditCard, FiMail, FiPhone, FiShield, FiUser } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/api';
import './ProfilePage.css';
import { useLanguage } from '../../context/LanguageContext';
import { getProfileAvatar, normalizeUserGender } from '../../utils/userAvatar';

export default function ProfilePage() {
  const { language, t } = useLanguage();
  const display = (value) => value || t('notAvailable');
  const navigate = useNavigate();
  const { user, updateUser, logout } = useAuth();
  const [profile, setProfile] = useState(user);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    apiService.getCurrentUser()
      .then((fresh) => {
        setProfile(fresh);
        updateUser(fresh);
      })
      .catch((err) => setError(err.message || 'Could not load your profile.'))
      .finally(() => setLoading(false));
    // The profile should refresh once when the page opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goBack = () => navigate(profile?.role === 'doctor' ? '/doctor-review' : '/dashboard');
  const signOut = () => {
    logout();
    navigate('/login');
  };
  const genderName = () => {
    const gender = normalizeUserGender(profile?.gender);
    if (gender === 'female') return t('female');
    if (gender === 'male') return t('male');
    if (gender === 'unspecified') return t('preferNotSay');
    return t('notAvailable');
  };

  const planName = () => {
    const plan = String(profile?.subscription_plan || (profile?.role === 'doctor' ? 'instructor' : 'starter')).toLowerCase();
    if (plan === 'instructor') return t('instructorPlan');
    if (plan === 'clinical_lab') return t('clinicalLabPlan');
    if (plan === 'cohort') return t('cohortPlan');
    if (plan === 'starter' || plan === 'free') return t('starterPlan');
    return plan.replace(/_/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase());
  };

  return (
    <main className="profile-page">
      <div className="profile-shell">
        <button type="button" className="profile-back" onClick={goBack}><FiArrowLeft /> {t('returnDashboard')}</button>
        {loading ? <div className="profile-state">{t('loadingProfile')}</div> : error ? (
          <div className="profile-state profile-error">{error}</div>
        ) : (
          <>
            <section className="profile-hero">
              <div className="profile-avatar-large">{getProfileAvatar(profile)}</div>
              <div>
                <span className="profile-eyebrow">{t('myProfile')}</span>
                <h1>{display(profile?.name)}</h1>
                <span className={`profile-role profile-role-${profile?.role || 'student'}`}>
                  <FiShield /> {profile?.role === 'doctor' ? t('doctor') : t('student')}
                </span>
              </div>
            </section>

            <section className="profile-card">
              <h2>{t('accountInformation')}</h2>
              <div className="profile-grid">
                <div className="profile-field"><FiUser /><span><small>{t('fullName')}</small>{display(profile?.name)}</span></div>
                <div className="profile-field"><FiMail /><span><small>{t('emailAddress')}</small>{display(profile?.email)}</span></div>
                <div className="profile-field"><FiPhone /><span><small>{t('phoneNumber')}</small>{display(profile?.phone)}</span></div>
                <div className="profile-field"><FiCalendar /><span><small>{t('dateOfBirth')}</small>{display(profile?.date_of_birth)}</span></div>
                <div className="profile-field"><FiUser /><span><small>{t('gender')}</small>{genderName()}</span></div>
                <div className="profile-field"><FiCreditCard /><span><small>{t('currentPlan')}</small>{planName()}</span></div>
                <div className="profile-field profile-field-wide"><FiShield /><span><small>{t('institution')}</small>{display(profile?.place_of_study)}</span></div>
                <div className="profile-field profile-field-wide"><FiCalendar /><span><small>{t('memberSince')}</small>{profile?.created_at ? new Date(profile.created_at).toLocaleDateString(language) : t('notAvailable')}</span></div>
                <div className="profile-field profile-field-wide"><FiShield /><span><small>{t('accountStatus')}</small>{profile?.enabled === false ? t('disabled') : t('enabled')}{profile?.provider ? ` - ${profile.provider}` : ''}</span></div>
                <div className="profile-field profile-field-wide"><FiUser /><span><small>{t('userId')}</small>{display(profile?.id)}</span></div>
              </div>
            </section>

            <section className="profile-actions">
              <button type="button" className="profile-primary" onClick={goBack}>{t('returnDashboard')}</button>
              <button type="button" className="profile-danger" onClick={signOut}>{t('signOut')}</button>
            </section>
          </>
        )}
      </div>
    </main>
  );
}
